<template>
  <section class="SummaryBar">
    <dl class="SummaryBar-items">
      <div
        v-for="item of items"
        :key="item.label"
        class="SummaryBar-item"
        :class="{ 'SummaryBar-item--highlighted': item.highlighted }"
      >
        <dt class="SummaryBar-label">
          {{ item.label }}
        </dt>
        <dd class="SummaryBar-value">
          {{ item.value }}
        </dd>
      </div>
    </dl>

    <div v-if="backLabel || submitLabel" class="SummaryBar-actions">
      <ActionButton
        v-if="backLabel"
        variant="tertiary"
        size="md"
        leftIcon="chevron-left"
        flipRtl
        class="SummaryBar-back"
        @click="emit('back')"
      >
        {{ backLabel }}
      </ActionButton>

      <ActionButton
        v-if="submitLabel"
        variant="secondary"
        size="md"
        rightIcon="arrow-right"
        flipRtl
        :disabled="submitDisabled"
        class="SummaryBar-submit"
        @click="emit('submit')"
      >
        {{ submitLabel }}
      </ActionButton>
    </div>
  </section>
</template>

<script setup lang="ts">
import ActionButton from '../../atoms/ActionButton/ActionButton.vue';
import type { SummaryBarEmits, SummaryBarProps } from './SummaryBar.model';

defineProps<SummaryBarProps>();
const emit = defineEmits<SummaryBarEmits>();
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .SummaryBar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: vars.$dimensions-d-6;
    padding-block: vars.$dimensions-d-5;
    padding-inline: vars.$dimensions-d-6;

    background-color: vars.$organisms-summarybar-bg-color;
    border: vars.$organisms-summarybar-border-stroke solid vars.$organisms-summarybar-border-color;
    border-radius: vars.$organisms-summarybar-lg-md-border-radius;

    @include responsive.mobile {
      flex-direction: column;
      align-items: stretch;
      padding: vars.$dimensions-d-3;
      border-radius: vars.$organisms-summarybar-sm-border-radius;
    }

    &-items {
      display: flex;
      flex-wrap: wrap;
      gap: vars.$dimensions-d-8;
      margin: 0;

      @include responsive.mobile {
        flex-direction: column;
        gap: vars.$dimensions-d-3;
      }
    }

    &-item {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-1;

      &--highlighted .SummaryBar-value {
        color: vars.$organisms-summarybar-summaryitem-highlighted-value-color;
      }
    }


    &-label {
      color: vars.$organisms-summarybar-summaryitem-label-color;
      text-transform: uppercase;
      @include text.text('xs');
    }

    &-value {
      margin: 0;
      color: vars.$organisms-summarybar-summaryitem-value-color;
      @include text.heading('h6');
    }

    &-actions {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-6;

      @include responsive.mobile {
        // The submit button comes first on mobile.
        flex-direction: column-reverse;
        align-items: stretch;
        gap: vars.$dimensions-d-2;
      }
    }
  }
}
</style>
